import type { ReactNode } from "react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { useCommandState } from "cmdk"
import Fuse from "fuse.js"
import {
	IconOffice3Outline18 as Building2,
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconPlusOutline18 as Plus,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"
import { FixedSizeList } from "react-window"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { PopoverProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Avatar,
	Button,
	IconButton,
	Popover,
	ScrollArea,
	ScrollViewport,
	Shortcut,
} from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { PartialContactCreate } from "../contacts/create-contact-dialog"
import type { PartialCustomerCreate } from "./create-customer-dialog"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { useContactOptions } from "@/lib/use-entity-options"
import { useRole } from "@/lib/useRole"
import { CreateContactDialog } from "../contacts/create-contact-dialog"
import { selectorActionClassName } from "../selector-action-button"
import { CompanyLogo } from "./company-logo"
import { CreateCustomerDialog } from "./create-customer-dialog"
import { filterCompanies } from "./filter-companies"
import { UpdateCustomerDialog } from "./update-customer-dialog"

// The fields the trigger renders for whichever customer is selected.
interface SelectedCompany {
	id: string
	name: string
	domains?: readonly string[] | null
	logoUrl?: string | null
	companyMeta?: { brandfetchData?: unknown } | null
}

interface CustomersSelectProps {
	companyId: string | null
	// A caller that already holds the row passes it here. Every thread query
	// relates its company with companyMeta, so passing it saves a synced query.
	company?: SelectedCompany | null
	onChange: (company: PartialCustomerCreate | null) => void
	className?: string
	align?: PopoverProps["align"]
	modal?: PopoverProps["modal"]
	withoutIcon?: boolean
	hideCompanyLogo?: boolean
	onContactSelect?: (contact: PartialContactCreate | null) => void
	trigger?: ReactNode
	hideEmptyStateText?: boolean
	showContacts?: boolean
}

export const CustomersSelect = memo(
	({
		onChange,
		companyId,
		company,
		className,
		align,
		modal,
		withoutIcon,
		hideCompanyLogo,
		onContactSelect,
		trigger,
		hideEmptyStateText = false,
		showContacts = false,
	}: CustomersSelectProps) => {
		const [open, onOpenChange] = useToggle(false)

		const [query, setQuery] = useState("")
		// The company list covers the whole workspace, so it stays unsynced until
		// the picker opens; the selected company comes from its own row instead.
		const [companies] = useQuery(queries.companiesWithMeta(), {
			enabled: open,
		})
		const { contacts } = useContactOptions({
			query,
			enabled: open && showContacts,
		})
		const [queriedCompany] = useQuery(
			queries.companyWithMetaById(companyId ?? ""),
			{ enabled: !!companyId && company === undefined },
		)
		const selectedCompany: SelectedCompany | undefined =
			company === undefined ? queriedCompany : (company ?? undefined)

		const [companyCreateValue, setCompanyCreateValue] = useState("")
		const [contactCreateValue, setContactCreateValue] = useState("")

		const filteredCompanies = useMemo(
			() => filterCompanies(companies, query),
			[companies, query],
		)

		const filteredContacts = useMemo(() => {
			if (contacts.length === 0) return []
			if (!query) return contacts
			const fuse = new Fuse(contacts, {
				keys: [
					{ name: "name", weight: 2 },
					{ name: "email", weight: 1 },
				],
				threshold: 0.3,
			})
			return fuse.search(query).map((result) => result.item)
		}, [contacts, query])

		const parentRef = useRef<HTMLDivElement>(null)
		const { role } = useRole()

		useShortcut(
			"meta+shift+m, ctrl+shift+m",
			() => {
				onOpenChange(true)
			},
			{
				enableOnContentEditable: true,
				preventDefault: true,
				enabled: role !== "VIEWER",
			},
		)

		const [isUpdateCompanyOpen, setIsUpdateCompanyOpen] = useToggle(false)

		const UpdateFormDialog = useCallback(() => {
			return (
				<>
					{selectedCompany && (
						<UpdateCustomerDialog
							isOpen={isUpdateCompanyOpen}
							onOpenChange={setIsUpdateCompanyOpen}
							values={{
								id: selectedCompany.id,
								name: selectedCompany.name,
								domains: [...(selectedCompany?.domains ?? [])],
							}}
						/>
					)}
				</>
			)
		}, [isUpdateCompanyOpen, selectedCompany, setIsUpdateCompanyOpen])

		const companiesListHeight = Math.min(filteredCompanies.length * 36, 180)
		const contactsListHeight = Math.min(filteredContacts.length * 36, 180)
		const navigate = useNavigate()

		return (
			<>
				<UpdateFormDialog />
				<CreateCustomerDialog
					defaultName={companyCreateValue}
					isOpen={!!companyCreateValue}
					onOpenChange={(open, newCompany) => {
						if (!open) {
							if (newCompany) {
								onChange(newCompany)
								onOpenChange(false)
								setQuery("")
								analytics.track("created-inline-company")
							}
							setCompanyCreateValue("")
							analytics.track("close-inline-create-company-dialog")
						}
					}}
				/>
				{showContacts && (
					<CreateContactDialog
						defaultValue={contactCreateValue}
						isOpen={!!contactCreateValue}
						onOpenChange={(open, newContact) => {
							if (!open) {
								if (newContact) {
									onContactSelect?.(newContact)
									onOpenChange(false)
									setQuery("")
									analytics.track("created-inline-contact")
								}
								setContactCreateValue("")
								analytics.track("close-inline-create-contact-dialog")
							}
						}}
					/>
				)}
				<div
					className={cn(
						"group relative flex min-w-52 items-center gap-0.5 truncate",
						className,
					)}
				>
					<Popover
						open={open}
						onOpenChange={onOpenChange}
						modal={modal}
						className="p-0"
						side="left"
						align={align}
						triggerTooltip={
							<div className="flex gap-1">
								Select customer
								<Shortcut className="ml-1">{ShortcutSymbols.CMD}</Shortcut>
								<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
								<Shortcut>{ShortcutSymbols.M}</Shortcut>
							</div>
						}
						content={
							<Command shouldFilter={false}>
								<div className="relative">
									<CommandInput
										autoFocus
										placeholder="Search..."
										value={query}
										onValueChange={setQuery}
										className={cn("max-w-[9.5rem] md:max-w-[11.5rem]", {
											"max-w-[9.5rem] md:max-w-[11.5rem]": query,
										})}
									/>
									<div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
										<div className="flex items-center gap-1.5">
											<Shortcut>⌘</Shortcut>
											<Shortcut>⇧</Shortcut>
											<Shortcut>M</Shortcut>
										</div>
									</div>
								</div>
								<CommandList ref={parentRef} className="max-h-none">
									{filteredCompanies.length > 0 && (
										<CommandGroup heading="Customers">
											{selectedCompany?.id && (
												<CommandItem
													onSelect={() => {
														onChange(null)
														onOpenChange(false)
													}}
												>
													<div className="flex items-center gap-2 px-1">
														<Building2 className="h-4 w-4" />
														<span className="flex-1 truncate">No customer</span>
													</div>
												</CommandItem>
											)}
											<ScrollArea>
												<FixedSizeList
													height={companiesListHeight}
													width="100%"
													itemCount={filteredCompanies.length}
													itemData={filteredCompanies}
													itemKey={(index, data) => data[index]?.id ?? index}
													itemSize={36}
													overscanCount={0}
													// TODO: upgrade react-window to v2 — @types/react-window@1.8 pulls
													// in @types/react@18 which conflicts with our react@19. Cast works
													// at runtime but the types are technically wrong. v2 ships its own.
													outerElementType={ScrollViewport as unknown as string}
												>
													{({ index, style }) => {
														const company = filteredCompanies[index]

														if (!company) return null

														return (
															<CommandItem
																value={company.id}
																onSelect={() => {
																	onChange(company)
																	onOpenChange(false)
																	setQuery("")
																}}
																style={style}
																key={company.id}
															>
																<div className="flex items-center gap-2">
																	<CompanyLogo
																		brandfetchData={
																			company.companyMeta
																				?.brandfetchData as BrandfetchData
																		}
																		fallback={company.name}
																		size="xs"
																		linearLogoUrl={
																			company?.logoUrl || undefined
																		}
																	/>
																	<span className="flex-1 truncate">
																		{company.name}
																	</span>
																	{companyId === company.id && (
																		<Check className="h-4 w-4" />
																	)}
																</div>
															</CommandItem>
														)
													}}
												</FixedSizeList>
											</ScrollArea>
										</CommandGroup>
									)}

									<AddNewItems
										showContacts={showContacts}
										onCreateCompany={(value) => {
											setCompanyCreateValue(value)
										}}
										onCreateContact={(value) => {
											setContactCreateValue(value)
										}}
									/>

									{showContacts && filteredContacts.length > 0 && (
										<>
											<CommandSeparator />
											<CommandGroup heading="People">
												<FixedSizeList
													height={contactsListHeight}
													width="100%"
													itemCount={filteredContacts.length}
													itemData={filteredContacts}
													itemKey={(index, data) => data[index]?.id ?? index}
													itemSize={36}
													overscanCount={5}
												>
													{({ index, style }) => {
														const contact = filteredContacts[index]

														if (!contact) return null

														return (
															<CommandItem
																value={`contact-${contact.id}`}
																onSelect={() => {
																	onContactSelect?.(contact)
																	onOpenChange(false)
																	setQuery("")
																}}
																style={style}
																key={contact.id}
															>
																<div className="flex items-center gap-2">
																	<Avatar
																		size="xs"
																		name={contact.name || contact.email}
																		colorSeed={contact.email}
																		image={contact.imageUrl}
																	/>
																	<span className="flex-1 truncate">
																		{contact.name
																			? contact.name
																			: contact.email}
																	</span>
																</div>
															</CommandItem>
														)
													}}
												</FixedSizeList>
											</CommandGroup>
										</>
									)}
								</CommandList>
							</Command>
						}
					>
						{trigger ? (
							trigger
						) : (
							<Button
								variant="ghost"
								{...(withoutIcon
									? {}
									: {
											Icon:
												companyId && selectedCompany ? undefined : Building2,
										})}
								className={cn(
									"h-auto flex-1 justify-start truncate text-xs hover:bg-gray-200 group-hover:bg-gray-200 dark:hover:bg-gray-800 dark:group-hover:bg-gray-800",
									companyId && selectedCompany
										? "!text-primary"
										: "!text-tertiary",
									role === "VIEWER" && "pointer-events-none",
								)}
							>
								{companyId && selectedCompany ? (
									<>
										{!hideCompanyLogo && (
											<CompanyLogo
												brandfetchData={
													selectedCompany.companyMeta
														?.brandfetchData as BrandfetchData
												}
												fallback={selectedCompany.name}
												size="xxs"
												className="mr-2"
												linearLogoUrl={selectedCompany?.logoUrl || undefined}
											/>
										)}

										<span className="max-w-40 truncate text-xs font-medium text-primary">
											{selectedCompany.name}
										</span>
									</>
								) : (
									<>
										{hideEmptyStateText ? null : (
											<span className="truncate font-medium">Set customer</span>
										)}
									</>
								)}
							</Button>
						)}
					</Popover>

					{selectedCompany ? (
						<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
							<IconButton
								Icon={ChevronRight}
								onClick={() => {
									void navigate({
										to: "/customers/$id",
										params: { id: selectedCompany.id },
									})
								}}
								variant="ghost"
								size="md"
								className={cn("px-1.5", selectorActionClassName)}
							/>
						</div>
					) : null}
				</div>
			</>
		)
	},
)

interface AddNewItemsProps {
	onCreateCompany: (value: string) => void
	onCreateContact: (value: string) => void
	showContacts: boolean
}

const AddNewItems = memo(
	({ onCreateCompany, onCreateContact, showContacts }: AddNewItemsProps) => {
		const search = useCommandState((state) => state.search).trim()

		if (!search) return null

		return (
			<CommandGroup>
				{showContacts && (
					<CommandItem
						key={`add-contact-${search}`}
						value={`add-contact-${search}`}
						onSelect={() => {
							analytics.track("open-inline-create-contact-dialog")
							onCreateContact(search)
						}}
					>
						<div className="flex items-center gap-2 truncate">
							<Plus className="size-3 min-h-3 min-w-3" />
							<div className="truncate">Create {search}</div>
							<span className="text-xs text-secondary">Person</span>
						</div>
					</CommandItem>
				)}
				<CommandItem
					key={`add-company-${search}`}
					value={`add-company-${search}`}
					onSelect={() => {
						analytics.track("open-inline-create-company-dialog")
						onCreateCompany(search)
					}}
				>
					<div className="flex items-center gap-2 truncate">
						<Plus className="size-3 min-h-3 min-w-3" />
						<div className="truncate">Create {search}</div>
						<span className="text-xs text-secondary">Customer</span>
					</div>
				</CommandItem>
			</CommandGroup>
		)
	},
)
